import { getCurrentInstance } from "vue";

/**
 * Shared helpers for EmployeesDirectoryTable and ResponsibilitiesDirectoryTable
 * to avoid code duplication and satisfy SonarQube quality gate.
 */

/** Cached app router — `getCurrentInstance()` is null in DOM handlers (e.g. table row click). */
let directoryTableRouterCache = null;

/**
 * Call from composable `setup()` / singleton creation when `getCurrentInstance()` is valid.
 */
export function primeDirectoryTableRouterCache() {
  const inst = getCurrentInstance()?.proxy ?? null;
  const r = inst?.$router || inst?.$root?.$router;
  if (r) {
    directoryTableRouterCache = r;
  }
}

/**
 * Router and current route for directory drilldowns.
 * On row clicks there is no component instance, so `$root.$route` is missing; Vue Router 3
 * exposes the active location via `router.currentRoute` (getter for `history.current`).
 * @returns {{ router: object | null, route: object | null }}
 */
export function resolveDirectoryTableRouterContext() {
  const inst = getCurrentInstance()?.proxy ?? null;
  const router = inst?.$router || inst?.$root?.$router || directoryTableRouterCache;
  if (router) {
    directoryTableRouterCache = router;
  }
  const route =
    router?.currentRoute || router?.history?.current || inst?.$route || inst?.$root?.$route || null;
  return { router: router || null, route };
}

/**
 * Maps a directory item (employee or responsibility) to props for EmployeeBadgeCounter.
 * @param {{ checkpoint?: { apprentice?: number, professional?: number, coach?: number, master?: number } }} item
 * @returns {Object}
 */
export function getEmployeeBadgeCounterProps(item) {
  const checkpoint = item?.checkpoint;
  return {
    count_of_apprentice: checkpoint?.apprentice ?? 0,
    count_of_proficient: checkpoint?.professional ?? 0,
    count_of_coach: checkpoint?.coach ?? 0,
    count_of_architect: checkpoint?.master ?? 0,
  };
}

/**
 * Maps directory badge rows (coach/master lists from API) to PeopleAvatarsButton items.
 * @param {Array<{ img?: string, avatar?: string, firstName?: string, lastName?: string }>} badges
 * @returns {Array<{ img: string|null, firstName: string, lastName: string }>}
 */
export function mapBadgesToPeopleAvatars(badges) {
  if (!Array.isArray(badges)) return [];
  return badges.map((b) => ({
    img: b.img ?? b.avatar ?? null,
    firstName: b.firstName ?? "",
    lastName: b.lastName ?? "",
  }));
}

/**
 * Directory API `coaches` rows → PeopleAvatarsButton items.
 * @param {Array<{ name?: string, avatar?: string|null }>} coaches
 */
export function mapDirectoryCoachesToAvatars(coaches) {
  if (!Array.isArray(coaches)) return [];
  return coaches.map((c) => {
    const full = String(c.name ?? "").trim();
    const parts = full.split(/\s+/).filter(Boolean);
    return {
      img: c.avatar ?? c.img ?? null,
      firstName: parts[0] ?? "",
      lastName: parts.slice(1).join(" ") ?? "",
    };
  });
}

/**
 * Directory API `masters` rows → PeopleAvatarsButton items.
 * @param {Array<{ name?: string, full_name?: string, avatar?: string|null }>} masters
 */
export function mapDirectoryMastersToAvatars(masters) {
  if (!Array.isArray(masters)) return [];
  return masters.map((m) => {
    const full = String(m.name ?? m.full_name ?? "").trim();
    const parts = full.split(/\s+/).filter(Boolean);
    return {
      img: m.avatar ?? m.img ?? null,
      firstName: parts[0] ?? "",
      lastName: parts.slice(1).join(" ") ?? "",
    };
  });
}

/**
 * Creates a setLoading function bound to the given state.loading object.
 * @param {Object} loadingState - reactive state.loading
 * @returns {function(string, boolean): void}
 */
export function createSetLoading(loadingState) {
  return (key, value) => {
    if (Object.prototype.hasOwnProperty.call(loadingState, key)) {
      loadingState[key] = value;
    }
  };
}
